import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface DropdownOption {
    label: string;
    onClick: () => void;
}

interface DropdownProps {
    children: ReactNode;
    options: Array<DropdownOption>;
    style?: any;
}

export default function Dropdown({ children, options, style }: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const closeDropdown = () => {
        setIsOpen(false);
    };

    const handleOptionClick = (onClick: () => void) => {
        onClick();
        closeDropdown();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                closeDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            <div
                role="button"
                tabIndex={0}
                onClick={toggleDropdown}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleDropdown();
                    }
                }}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                {children}
            </div>

            {isOpen && (
                <div
                    className="dropdown-content absolute right-0 mt-2 min-w-[12rem] rounded-2xl p-2"
                    style={{ zIndex: 49, ...(style || {}) }}
                >
                    <div className="menu rounded-xl" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {options.map((option, index) => (
                            <div
                                key={index}
                                onClick={() => handleOptionClick(option.onClick)}
                                className="sf-menu-item cursor-pointer rounded-xl px-3 py-2.5 text-start text-sm font-semibold text-base-content/75"
                                role="menuitem"
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
